import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

export interface UserDirectoryEntry {
  id: number;
  username: string;
  fullName: string;
  imageFile: string | null;
}

/**
 * Felhasználó-névjegyzék a profilképekhez.
 *
 * A támogatás, a munkaidő és az üzenőfal SZÖVEGES névvel azonosítja a
 * szerzőt (nem user id-val), ezért a névjegyzéket név szerint is indexeljük.
 * A lista alkalmazásonként EGYSZER töltődik le (shareReplay), a képek blobjait
 * pedig fájlnév szerint gyorsítótárazzuk — enélkül egy 30 kártyás support
 * képernyő 30-szor töltené le ugyanazt a képet.
 */
@Injectable({ providedIn: 'root' })
export class UserDirectoryService {
  private directory$?: Observable<UserDirectoryEntry[]>;
  private byName = new Map<string, UserDirectoryEntry>();
  private imageUrlCache = new Map<string, ReplaySubject<string | null>>();

  constructor(private http: HttpClient) {}

  /** A teljes névjegyzék (egyszer töltődik le, utána gyorsítótárból jön). */
  list(): Observable<UserDirectoryEntry[]> {
    if (!this.directory$) {
      this.directory$ = this.http.get<UserDirectoryEntry[]>('users/directory').pipe(
        // Hiba esetén üres névjegyzék: a felület ilyenkor az iniciálékat mutatja
        // tovább, nem törik el a képernyő.
        catchError(() => of([] as UserDirectoryEntry[])),
        tap((entries) => this.index(entries)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.directory$;
  }

  /** Egy névhez (teljes név vagy felhasználónév) tartozó profilkép fájlneve. */
  imageFileFor(name: string | null | undefined): Observable<string | null> {
    const key = this.nameKey(name);
    if (!key) {
      return of(null);
    }
    return this.list().pipe(map(() => this.byName.get(key)?.imageFile || null));
  }

  /**
   * Megjeleníthető (blob) URL egy profilkép fájlnévhez. A kép auth-fejléccel
   * jön, ezért nem tehető közvetlenül `src`-be.
   */
  imageUrl(imageFile: string | null | undefined): Observable<string | null> {
    const fileName = `${imageFile || ''}`.trim();
    if (!fileName) {
      return of(null);
    }
    const existing = this.imageUrlCache.get(fileName);
    if (existing) {
      return existing.asObservable();
    }
    const cached = new ReplaySubject<string | null>(1);
    this.imageUrlCache.set(fileName, cached);
    this.http.get(`users/images/${fileName}`, { responseType: 'blob' }).subscribe({
      next: (blob) => cached.next(URL.createObjectURL(blob)),
      error: () => cached.next(null),
    });
    return cached.asObservable();
  }

  /** A névjegyzék újratöltése (pl. profilkép feltöltése után). */
  invalidate(): void {
    this.directory$ = undefined;
    this.byName.clear();
    this.imageUrlCache.forEach((subject) => subject.complete());
    this.imageUrlCache.clear();
  }

  private index(entries: UserDirectoryEntry[]): void {
    this.byName.clear();
    for (const entry of entries) {
      const fullNameKey = this.nameKey(entry.fullName);
      const usernameKey = this.nameKey(entry.username);
      if (fullNameKey) {
        this.byName.set(fullNameKey, entry);
      }
      // A felhasználónevet csak akkor indexeljük, ha nem üt teljes névvel —
      // különben egy „admin" felhasználónév elfedhetne egy valódi nevet.
      if (usernameKey && !this.byName.has(usernameKey)) {
        this.byName.set(usernameKey, entry);
      }
    }
  }

  private nameKey(name: string | null | undefined): string {
    return `${name || ''}`.trim().toLowerCase();
  }
}
